import clsx from 'clsx';
import dayjs from 'dayjs';
import { useField } from 'formik';
import React, { useEffect, useRef, useState } from 'react';

import CalendarIcon from '@components/Images/Calendar';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDateTimePicker } from '@mui/x-date-pickers/StaticDateTimePicker';
import { renderDesktopDateTimeView } from '@mui/x-date-pickers/dateTimeViewRenderers';
import {
  DEFAULT_POLL_EXPIRATION_DAYS,
  DEFAULT_POLL_EXPIRATION_HOUR,
  DEFAULT_POLL_EXPIRATION_MINUTE,
  formatPollExpiresAt,
  parsePollExpiresAt,
} from '@utils/backend-api/polls';

import FieldError from '../FieldError';
import FieldLabel from '../FieldLabel';
import './styles.scss';

const LOCAL_DEFAULT_TIME = {
  hour: DEFAULT_POLL_EXPIRATION_HOUR,
  minute: DEFAULT_POLL_EXPIRATION_MINUTE,
};

const desktopViewRenderers = {
  day: renderDesktopDateTimeView,
  month: renderDesktopDateTimeView,
  year: renderDesktopDateTimeView,
  hours: renderDesktopDateTimeView,
  minutes: renderDesktopDateTimeView,
  seconds: renderDesktopDateTimeView,
  meridiem: renderDesktopDateTimeView,
};

function applyTime(value: dayjs.Dayjs, time: { hour: number; minute: number }) {
  const date = value.toDate();
  date.setHours(time.hour, time.minute, 0, 0);
  return dayjs(date);
}

interface DateTimeRangePickerFieldProps {
  className?: string;
  calendarClassName?: string;
  darkMode?: boolean;
  disabled?: boolean;
  hint?: string;
  label?: string;
  name: string;
  minDate?: Date;
  maxDate?: Date;
  locale?: string;
  showNeighboringMonth?: boolean;
  tileDisabled?: ({ date }: { date: Date }) => boolean;
  defaultTime?: { hour: number; minute: number };
  initializeDefaultTime?: boolean;
}

const DateTimeRangePickerField = (props: DateTimeRangePickerFieldProps) => {
  const {
    label,
    name,
    darkMode = false,
    defaultTime = LOCAL_DEFAULT_TIME,
    className,
    ...calendarProps
  } = props;
  const [field, meta, helpers] = useField(name);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const pickerValue =
    parsePollExpiresAt(field.value) ||
    applyTime(dayjs().add(DEFAULT_POLL_EXPIRATION_DAYS, 'day'), defaultTime);

  const darkTheme = createTheme({
    palette: {
      mode: 'dark',
      primary: { main: '#F32A52' },
      background: { default: '#2F3538', paper: '#2F3538' },
      text: { primary: '#FFFFFF', secondary: '#FFFFFF', disabled: '#A8A8A8' },
    },
    components: {
      MuiMultiSectionDigitalClockSection: {
        styleOverrides: {
          root: {
            overflowY: 'auto',
            minWidth: 56,
            color: '#FFFFFF',
            '&::after': {
              display: 'none !important',
              height: '0 !important',
              maxHeight: '0 !important',
            },
          },
          item: {
            color: '#FFFFFF',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: '#FFFFFF',
          },
        },
      },
    } as object,
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsCalendarOpen(false);
      }
    };

    if (!isCalendarOpen) return undefined;
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCalendarOpen]);

  useEffect(() => {
    if (!isCalendarOpen) return undefined;

    const clampClockScroll = () => {
      containerRef.current
        ?.querySelectorAll('.MuiMultiSectionDigitalClockSection-root')
        .forEach((section) => {
          const el = section as HTMLElement;
          const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
          if (el.scrollTop > maxScroll) {
            el.scrollTop = maxScroll;
          }
        });
    };

    const frame = window.requestAnimationFrame(clampClockScroll);
    const timeout = window.setTimeout(clampClockScroll, 0);
    document.addEventListener('scroll', clampClockScroll, true);

    return () => {
      document.removeEventListener('scroll', clampClockScroll, true);
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
    };
  }, [isCalendarOpen, pickerValue]);

  const handleDateTimeChange = (value: dayjs.Dayjs | null) => {
    if (!value) return;

    const current = parsePollExpiresAt(field.value);
    const dateChanged = !current || !value.isSame(current, 'day');
    const next =
      dateChanged && current
        ? applyTime(value, { hour: current.hour(), minute: current.minute() })
        : dateChanged
        ? applyTime(value, defaultTime)
        : value;

    helpers.setValue(formatPollExpiresAt(next.toDate()));
  };

  const picker = (
    <StaticDateTimePicker
      displayStaticWrapperAs="desktop"
      ampm
      minutesStep={1}
      // @ts-expect-error timeSteps is supported at runtime for the digital clock
      timeSteps={{ hours: 1, minutes: 1 }}
      value={pickerValue}
      referenceDate={pickerValue}
      minDate={calendarProps.minDate ? dayjs(calendarProps.minDate).startOf('day') : undefined}
      maxDate={calendarProps.maxDate ? dayjs(calendarProps.maxDate).endOf('day') : undefined}
      disabled={calendarProps.disabled}
      onChange={(value) => handleDateTimeChange(value as dayjs.Dayjs | null)}
      onAccept={(value) => {
        handleDateTimeChange(value as dayjs.Dayjs | null);
        setIsCalendarOpen(false);
      }}
      viewRenderers={desktopViewRenderers as never}
      slotProps={{
        layout: {
          sx: {
            '& .MuiMultiSectionDigitalClockSection-root': {
              overflowY: 'auto',
              minWidth: 56,
              color: '#FFFFFF',
            },
            '& .MuiMultiSectionDigitalClockSection-root::after': {
              display: 'none !important',
              height: '0 !important',
            },
            '& .MuiMultiSectionDigitalClockSection-item, & .MuiMenuItem-root': {
              color: '#FFFFFF',
            },
          },
        },
      }}
    />
  );

  return (
    <div className={className}>
      <div className="relative" ref={containerRef}>
        {label && <FieldLabel htmlFor={name}>{label}</FieldLabel>}
        <div className="relative">
          <input
            id={name}
            value={field.value || ''}
            onClick={() => {
              if (!calendarProps.disabled) setIsCalendarOpen((open) => !open);
            }}
            onChange={(event) => event.preventDefault()}
            disabled={calendarProps.disabled}
            readOnly
            type="text"
            className="h-[50px] w-full rounded-none border border-solid border-grayscale-beige px-8 py-16 font-suisse-intl text-overline-md text-black placeholder-grayscale-gray focus:outline-none disabled:bg-white disabled:text-grayscale-gray disabled:opacity-40"
          />
          <CalendarIcon className="pointer-events-none absolute right-0 top-1/2 h-[18px] w-[18px] -translate-x-1/2 -translate-y-1/2 text-black" />
        </div>
        {isCalendarOpen && (
          <div className={clsx('calendar-dropdown', darkMode && 'dark-mode')}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              {darkMode ? <ThemeProvider theme={darkTheme}>{picker}</ThemeProvider> : picker}
            </LocalizationProvider>
          </div>
        )}
      </div>
      {meta.touched && meta.error && <FieldError>{meta.error}</FieldError>}
    </div>
  );
};

export default DateTimeRangePickerField;
